import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import * as NotificationsActions from "src/app/store/notifications/notifications.actions";
import { Observable, Subscription, filter } from 'rxjs';
import { Scenery } from 'src/app/model/scenery';
import { selectSceneries } from 'src/app/store/scenery/scenery.selector';
import { selectSchedules } from 'src/app/store/schedule/schedule.selector';
import { Schedule } from 'src/app/model/schedule';
import { CUSTOMERS_PAGE_DEFAULT_SIZE } from 'src/app/conf/customer';
import { ScheduleDialogComponent } from '../schedule-dialog/schedule.dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ConfirmationService } from 'primeng/api';
import * as ScheduleActions from 'src/app/store/schedule/schedule.actions';
import { WEEK_DAYS } from 'src/app/conf/schedule';
import { Site } from 'src/app/model/site';
import { selectCurrentSite } from 'src/app/store/site/site.selector';
import { StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { selectCalendars } from 'src/app/store/calendar/calendar.selector';
import { Calendar } from 'src/app/model/calendar';

@Component({
	selector: 'app-schedules-list',
	templateUrl: './schedules-list.component.html',
	providers: [ConfirmationService, DialogService]
})

export class SchedulesListComponent implements OnInit, OnDestroy {
	// Observables and subscriptions
	sceneries$: Observable<Scenery[] | undefined>;
	sceneriesSub: Subscription | undefined;
	schedules$: Observable<Schedule[] | undefined>;
	schedulesSub: Subscription | undefined;
	site$: Observable<Site | undefined>;
	siteSub: Subscription | undefined;
	calendars$: Observable<Calendar[] | undefined>;
	calendarsSub: Subscription | undefined;

	sceneries: Scenery[];
	schedules: Schedule[];
	calendars: Calendar[];
	currentSite: Site;

	ref: DynamicDialogRef | undefined;

	schedulesLoading: boolean;
	totalRecords = 0;
	pageSize: number = CUSTOMERS_PAGE_DEFAULT_SIZE;
	weekDays = WEEK_DAYS;

	screenWidth: number;

	acceptButtonStyle: string;
	rejectButtonStyle: string;

	constructor(
		private store: Store<AppState>,
		private translate: TranslateService,
		public dialogService: DialogService,
		public confirmationService: ConfirmationService,
	) {
		this.initSelectors();
	}

	ngOnInit() {
		this.schedulesLoading = true;
		this.store.dispatch(NotificationsActions.setTopbarTitleId({
			stringId: 'SCENARIOS'
		}));
		this.subscribeAll();
		this.onResize();
	}

	ngOnDestroy(): void {
		this.sceneriesSub?.unsubscribe();
		this.schedulesSub?.unsubscribe();
		this.siteSub?.unsubscribe();
		this.calendarsSub?.unsubscribe();
	}

	initSelectors(): void {
		// Store selectors
		this.sceneries$ = this.store.select(selectSceneries);
		this.schedules$ = this.store.select(selectSchedules);
		this.site$ = this.store.select(selectCurrentSite);
		this.calendars$ = this.store.select(selectCalendars);
	}

	subscribeAll(): void {
		this.calendarsSub = this.calendars$?.subscribe((calendars) => {
			this.calendars = [...calendars];
		});
		this.sceneriesSub = this.sceneries$?.subscribe(
			(sceneries) => {
				this.sceneries = sceneries;
			}
		);
		this.schedulesSub = this.schedules$?.subscribe(
			(schedules) => {
				this.schedules = schedules;
				this.totalRecords = schedules.length;
				this.schedulesLoading = false;
			}
		);
		this.siteSub = this.site$?.pipe(filter(site => !!site))
			.subscribe(site => {
				this.currentSite = site;
			});
	}

	getBgColorSchedule(schedules: Schedule): any {
		return (schedules.Color) ? { 'background-color': schedules.Color, 'opacity': '50%' } : '';
	}

	getHeightColorSchedule(): string {
		/* Mobile */
		if (this.screenWidth <= 960) {
			return "w-20rem h-1rem mx-auto border-noround-top flex align-items-center justify-content-center";
		}
		/* Tablet */
		if (this.screenWidth <= 1137) {
			return "w-1rem h-7rem border-noround-right flex align-items-center justify-content-start";
		}
		/* Desktop */
		return "w-1rem h-4_5rem border-noround-right flex align-items-center justify-content-start";
	}

	getSceneryName(sceneryId: number): string {
		const scenery = this.sceneries.find(scen => scen.Id === sceneryId);
		if (!scenery) {
			return '';
		}
		return scenery.Name;
	}

	getStartingTime(schedules: Schedule): string {
		let min = new String(schedules.Minutes);
		if (min.length === 1) {
			min = '0' + min;
		}
		return schedules.Hours + ':' + min + ' | ' + schedules.Tz;
	}

	getStartEndDate(schedules: Schedule): string {
		const endDate = (schedules.End === null || schedules.End === undefined) ? this.translate.instant('DATE_NOT_DEFINED') : schedules.End.substring(0, 10);
		return schedules.Start.substring(0, 10) + ' - ' + endDate;
	}

	getTypeRepetition(schedules: Schedule): string {
		switch (schedules.EveryKey) {
			case 'days': {
				return this.translate.instant('DAILY');
			}
			case 'weeks': {
				return this.translate.instant('WEEKLY');
			}
			case 'months': {
				return this.translate.instant('MONTHLY');
			}
			default: {
				return this.translate.instant('NOT_REPEATING');
			}
		}
	}

	getColorTag(schedules: Schedule): string {
		switch (schedules.EveryKey) {
			case 'days': {
				return 'w-10rem p-tag-lightBlue0220';
			}
			case 'weeks': {
				return 'w-10rem p-tag-lightBlue0240';
			}
			case 'months': {
				return 'w-10rem p-tag-lightBlue0260';
			}
			default: {
				return 'w-10rem p-tag-darkBlue10';
			}
		}
	}

	getFrequencyString(valueFrequence: number, typeFrequence: string): string {
		if (valueFrequence === 1) {
			switch (typeFrequence) {
				case 'days':
					return this.translate.instant('EVERY') + ' ' + this.translate.instant('day');
				case 'weeks':
					return this.translate.instant('EVERY') + ' ' + this.translate.instant('week');
				case 'months':
					return this.translate.instant('EVERY') + ' ' + this.translate.instant('month');
				default:
					return "";
			}
		} else if (valueFrequence > 1) {
			switch (typeFrequence) {
				case 'days':
					return this.translate.instant('EVERY') + ' ' + valueFrequence + ' ' + this.translate.instant('days');
				case 'weeks':
					return this.translate.instant('EVERY') + ' ' + valueFrequence + ' ' + this.translate.instant('weeks');
				case 'months':
					return this.translate.instant('EVERY') + ' ' + valueFrequence + ' ' + this.translate.instant('months');
				default:
					return "";
			}
		}
		return "";
	}

	getFrequency(schedules: Schedule): string {
		switch (schedules.EveryKey) {
			case 'days': {
				return this.getFrequencyString(schedules.EveryValue, "days");
			}
			case 'weeks': {
				const weekDaysArray = schedules.WeekDays.split(',');
				const formatWeekDays = weekDaysArray.map(d => this.translate.instant(this.weekDays.find(day => day.Value === d).Name)).join(', ');
				return this.getFrequencyString(schedules.EveryValue, "weeks") + ', ' + this.translate.instant('ON_WEEKDAYS') + ': ' + formatWeekDays;
			}
			case 'months': {
				return this.getFrequencyString(schedules.EveryValue, "months") + ', ' + this.translate.instant('ON_DAY') + ' ' + schedules.MonthDay;
			}
			default: {
				return '';
			}
		}
	}


	getExclusionsCalendar(schedules: Schedule): string {
		if (schedules && schedules.CalendarId && this.calendars && this.calendars.length > 0) {
			const calFind = this.calendars.find(cal => cal.Id === schedules.CalendarId)
			return calFind ? calFind.Name : '';
		}
		return '';
	}

	addSchedule(): void {
		this.ref = this.dialogService.open(ScheduleDialogComponent, {
			header: this.translate.instant('ADD_SCHEDULE'),
			width: '80vw',
			breakpoints: { '1600px': '98vw' },
			// resizable: true,
			contentStyle: { overflow: 'auto' },
		});
	}

	editSchedule(schedule: Schedule): void {
		this.ref = this.dialogService.open(ScheduleDialogComponent, {
			header: this.translate.instant('EDIT_SCHEDULE'),
			width: '80vw',
			breakpoints: { '1600px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				schedule: schedule,
			},
		});
	}

	deleteSchedule(schedule: Schedule): void {
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.translate.instant('DELETE_SCHEDULE'),
			message: this.translate.instant('DELETE_SCHEDULE_WARNING'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				this.store.dispatch(ScheduleActions.deleteSchedule({ scheduleId: schedule.Id }));
			},
			reject: () => {
			}
		});
	}

	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}
}